import React from 'react';
import styled from 'styled-components';

interface ResponsiveBackButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'minimal';
  className?: string;
}

const StyledBackButton = styled.button<{ variant: string }>`
  /* Base styles */
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Variant-specific styles */
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: #3b82f6;
          color: white;
          border: 2px solid #2563eb;
          
          &:hover {
            background: #2563eb;
            border-color: #1d4ed8;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: rgba(255, 255, 255, 0.15);
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.25);
          
          &:hover {
            background: rgba(255, 255, 255, 0.25);
            border-color: rgba(255, 255, 255, 0.4);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
          }
        `;
      case 'minimal':
      default:
        return `
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
          
          &:hover {
            background: #e5e7eb;
            border-color: #9ca3af;
          }
        `;
    }
  }}
  
  /* Responsive sizing and positioning */
  @media (min-width: 1200px) {
    /* Large screens - full size */
    padding: 12px 24px;
    font-size: 16px;
    min-width: auto;
  }
  
  @media (min-width: 768px) and (max-width: 1199px) {
    /* Medium screens - slightly smaller */
    padding: 10px 20px;
    font-size: 15px;
    min-width: auto;
  }
  
  @media (min-width: 480px) and (max-width: 767px) {
    /* Small screens - compact */
    padding: 8px 16px;
    font-size: 14px;
    min-width: auto;
  }
  
  @media (max-width: 479px) {
    /* Extra small screens - very compact */
    padding: 6px 12px;
    font-size: 13px;
    min-width: auto;
  }
  
  /* Special handling for very narrow screens */
  @media (max-width: 400px) {
    /* Ultra narrow - minimal text */
    padding: 6px 8px;
    font-size: 12px;
  }
  
  /* Ensure button doesn't overflow container */
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResponsiveBackButton: React.FC<ResponsiveBackButtonProps> = ({
  onClick,
  children,
  variant = 'minimal',
  className
}) => {
  return (
    <StyledBackButton
      onClick={onClick}
      variant={variant}
      className={className}
    >
      <span className="text">{children}</span>
    </StyledBackButton>
  );
};

export default ResponsiveBackButton;
